import React, { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useCreateAffiliate } from "@/hooks/use-affiliates";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/Card";

export function AffiliateCreatePage() {
  const navigate = useNavigate();
  const createMutation = useCreateAffiliate();

  const [form, setForm] = useState({
    name: "",
    status: "active",
    defaultCurrencyId: "USD",
    internalNotes: "",
    hasNotifications: true,
    enableMediaCostTrackingLinks: true,
  });

  const [error, setError] = useState("");

  function updateField(field: string, value: string | boolean) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    try {
      const result = await createMutation.mutateAsync(form);
      navigate({ to: "/affiliates/$id", params: { id: String(result.data.id) } });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create affiliate");
    }
  }

  return (
    <div>
      <PageHeader title="Add Affiliate" description="Register a new affiliate partner" backHref="/affiliates" />

      <form onSubmit={handleSubmit} className="max-w-3xl space-y-6">
        {error && (
          <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">
            {error}
          </div>
        )}

        <Card title="Basic Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Affiliate Name *</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder="e.g., TopMedia Partners"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => updateField("status", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select
                value={form.defaultCurrencyId}
                onChange={(e) => updateField("defaultCurrencyId", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>
        </Card>

        <Card title="Settings">
          <div className="space-y-4">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={form.hasNotifications}
                onChange={(e) => updateField("hasNotifications", e.target.checked)}
                className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
              />
              <span className="text-sm text-gray-700">Enable notifications</span>
            </label>

            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={form.enableMediaCostTrackingLinks}
                onChange={(e) => updateField("enableMediaCostTrackingLinks", e.target.checked)}
                className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
              />
              <span className="text-sm text-gray-700">Enable media cost tracking links</span>
            </label>
          </div>
        </Card>

        <Card title="Internal Notes">
          <textarea
            value={form.internalNotes}
            onChange={(e) => updateField("internalNotes", e.target.value)}
            rows={4}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            placeholder="Optional internal notes about this affiliate..."
          />
        </Card>

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate({ to: "/affiliates" })}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="px-6 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors"
          >
            {createMutation.isPending ? "Creating..." : "Add Affiliate"}
          </button>
        </div>
      </form>
    </div>
  );
}
